import React, { createContext, useState } from 'react';
import Modal from "../MuiComponents/Modal";
import SuccessIcon from "./SuccessIcon";
import Typography from "../Typography";
import Button from "../Button";
import ErrorIcon from "./ErrorIcon";
import ConfirmCrossIcon from "./ConfirmCrossIcon";
import { MuiTextField } from '../MuiComponents/FormInputs';
import { address, dataSpaceValidation, domain, email, nameValidation, required_message, website_url } from "../../utils/validations";
import WarningIcon from './WarningIcon';

/** Strip noisy API prefixes so the modal reads as a single “Error:” sentence (ROAR-4314). */
function normalizeErrorModalDescription(text, title) {
    let s = String(text ?? "").replace(/\s+/g, " ").trim();
    if (!s) return "Something went wrong.";
    s = s.replace(/^schedule\s*invalid\s*:?\s*/i, "").trim();
    const t = String(title || "").trim().toLowerCase();
    if (t === "error" || t === "") {
        s = s.replace(/^error\s*:?\s*/i, "").trim();
    }
    return s || String(text ?? "").trim() || "Something went wrong.";
}

export const AlertContext = createContext()
const initialState = {
    open: false,
    title: null,
    variant: null,
    description: null,
    hideCloseIcon: false,
    onSubmit: () => {
    },
    onClose: () => {
    },
    okayText: null,
    cancelText: null
}

const AlertProvider = ({ children }) => {
    const [state, setState] = useState(initialState)
    const { variant, cancelText, okayText, hideCloseIcon, formOptions } = state;
    const [justificationData, setJustificationData] = useState('')
    const [disabledJustification, setDisabledJustification] = useState(true);
    const [errorMessage, updateErrorMessage] = useState(null);
    
    const validateJustification = (justificationData) => {
        return !dataSpaceValidation.test(justificationData) && justificationData.length === 0;
    }

    const [fieldValue, updateFieldValue] = useState("");
    const popup = (options) => {
        return new Promise((resolve, reject) => {
            setState({
                ...state,
                ...options,
                open: true,
                onSubmit: resolve,
            })
        })
    }
    const shouldHideOkayButton = state.hideOkayButton || false; // Default value is false if the prop is not provided

    const onClose = () => {
        initialState.hideOkayButton = shouldHideOkayButton
        setJustificationData("")
        updateErrorMessage("");
        setState(initialState)
    }
    const onOkay = () => {
        if (state.justification) {
            state.setValue("justification", fieldValue);
        }
        state.onSubmit()
        setJustificationData("")
        setState(initialState)
    }
    const disabled = justificationData.length === 0
    const dataSpaceValidation = /^(?:\S.*\S|\S?)$/gm;
    const isErrorConfirm = variant === "error" && Boolean(cancelText);
    const isErrorDismissOnly = variant === "error" && !cancelText;
    /**
     * ROAR-4944: the destructive-confirm layout (the Unsubscribe modal).
     *
     * Deliberately NOT a tweak of `warning`: the design inverts the usual emphasis. The
     * DESTRUCTIVE action is the resolving one (it must be, since `popup` only ever resolves on
     * okay) but it is drawn as a quiet red outline on the LEFT, while the harmless dismissal is
     * the filled primary button on the RIGHT — so the eye lands on "keep what you have". Every
     * other variant draws the resolving button filled and last, so reusing one of them here would
     * have made the destructive choice the visually recommended one.
     *
     * It also carries no icon and left-aligns its text, matching the frame.
     */
    const isDestructiveConfirm = variant === "destructive_confirm";
    return (
        <AlertContext.Provider value={[popup, onClose]}>
            {children}
            <Modal open={state.open}
                style={{ zIndex: 9999 }}
                /* The frame draws no close affordance. Passed as a Modal prop rather than through
                   `hideCloseIcon` state, which ALSO withholds `onClose` and would take backdrop
                   and Escape dismissal away with it. */
                hideCloseIcon={isDestructiveConfirm}
                {...{
                    ...(!hideCloseIcon ? { onClose } : {})
                }}
                PaperProps={{ className: variant === 'justification' ? '!min-w-[423px]' : variant == 'alt_names' ? '!min-w-[523px]' :  `!max-w-[523px]` }}>
                {variant === "success" && (
                    <div className="flex w-full justify-center">
                        <SuccessIcon />
                    </div>
                )}
                {variant === "warning" && (
                    <div className="flex w-full justify-center">
                        <ErrorIcon />
                    </div>
                )}
                {isErrorConfirm && (
                    <div className="flex w-full justify-center">
                        <ConfirmCrossIcon />
                    </div>
                )}
                {isErrorDismissOnly && (
                    <div className="flex w-full justify-center">
                        <ErrorIcon />
                    </div>
                )}
                {["warning!"].includes(variant) && <WarningIcon />}
                {isErrorDismissOnly ? (
                    /** ROAR-4314: single-line “Error: message” for dismiss-only API failures */
                    <Typography variant="body1" className="px-2.5 pb-1 text-left leading-relaxed text-gray-800">
                        <span className="font-semibold text-gray-900">
                            {(state.title || "Error").replace(/\s*:?\s*$/, "")}:
                        </span>{" "}
                        {normalizeErrorModalDescription(state.description, state.title)}
                    </Typography>
                ) : (
                    <>
                        {state.title ? (
                            <Typography
                                variant={isDestructiveConfirm ? "h6" : "h4"}
                                className={
                                    isDestructiveConfirm
                                        ? "text-headings pb-2.5 px-2.5 font-bold"
                                        : variant === "success" || isErrorConfirm || variant === "warning"
                                            ? "text-primary-800 pb-2.5 px-2.5 text-center"
                                            : "text-primary-800 pb-2.5 px-2.5"
                                }
                            >
                                {state.title}
                            </Typography>
                        ) : null}
                        {state.description ? (
                            <Typography
                                variant={"body1"}
                                className={
                                    isDestructiveConfirm
                                        ? "px-2.5 text-gray-800 leading-relaxed"
                                        : variant === "success" || isErrorConfirm || variant === "warning"
                                            ? "px-2.5 text-center text-gray-700"
                                            : "px-2.5 text-gray-700"
                                }
                            >
                                {state.description}
                            </Typography>
                        ) : null}
                    </>
                )}
                {variant === "justification" ? (
                   <>
                   <form onSubmit={formOptions.handleSubmit(onOkay)}>
                        <MuiTextField
                            label={"Enter justification here..."}
                            multiline
                            rows={3}
                            value = {justificationData}
                            InputProps={{
                                ...formOptions.register("justification", { required: required_message, ...dataSpaceValidation }),
                                onChange: (e) => {
                                const val = e.target.value;
                                const dataValidation = e.target.value.length === 0;
                                const spaceValidation = !dataSpaceValidation.test(e.target.value);
                                if(dataValidation) {
                                    updateErrorMessage("Justification is required");
                                    setDisabledJustification(true);
                                } else if (spaceValidation) {
                                    updateErrorMessage("Entered value cant start/end or contain only white spacing");
                                    setDisabledJustification(true);
                                } else {
                                    updateErrorMessage("");
                                    setDisabledJustification(false);
                                }
                                setJustificationData(val); // updating the default justification
                            }
                            }}
                            inputProps = {{
                                maxLength: 2000
                            }}
                            error={errorMessage}
                        />
                        <p className='justification-error'>{errorMessage}</p>
                        <div className={"text-center mt-6"}>
                        {cancelText && <Button variant={"outlined"} size={"small"} onClick={onClose}
                                            className={"mt-2.5 mr-2.5"}>{cancelText || "Cancel"}</Button>}
                        <Button disabled={disabledJustification} className={"mt-2.5"}>{okayText || "Okay"}</Button>
                        </div>
                    </form>
                    </>
                ) : (
                    <div className={"flex justify-center gap-x-4 mt-6"}>
                        {isDestructiveConfirm ? (
                            <>
                                {!shouldHideOkayButton && (
                                    <Button
                                        type={"button"}
                                        variant={"outlined"}
                                        onClick={onOkay}
                                        className={"mt-2.5 !border-functional-red !text-functional-red"}
                                    >
                                        {okayText || "Okay"}
                                    </Button>
                                )}
                                {cancelText && (
                                    <Button type={"button"} onClick={onClose} className={"mt-2.5"}>
                                        {cancelText || "Cancel"}
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                {cancelText && <Button variant={"outlined"} size={"small"} onClick={onClose}
                                    className={"mt-2.5"}>{cancelText || "Cancel"}</Button>}
                                {!shouldHideOkayButton && (
                                        <Button onClick={onOkay} className={"mt-2.5"}>
                                            {okayText || "Okay"}
                                        </Button>
                                )}
                            </>
                        )}
                    </div>
                )
                }
            </Modal>
        </AlertContext.Provider>
    );
};

export default AlertProvider;

